/**
 * Consumer Wallet · My Trust Wallet (premium view).
 *
 * Route: /wallet/passport/:consumer_id
 *
 * The consumer's consolidated trust profile — credentials grouped by
 * category, connected institutions, sharing history. External branding
 * remains "Consumer Trust Wallet"; Trust Wallet is the premium
 * portable-profile artefact within it.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { format, formatDistanceToNow } from "date-fns";
import {
  Shield, ShieldCheck, IdCard, Book, GraduationCap, Wallet,
  Briefcase, Award, Building2, Link2, Copy, Ban, Share2, ClipboardCheck,
  UserCog, ArrowRight, Info, FileText, Users, CalendarClock, CheckCircle2,
} from "lucide-react";
import { WalletShell } from "@/components/wallet/WalletShell";
import { loadWallet } from "@/lib/wallet-storage";
import PassportReviewNudge from "@/components/wallet/PassportReviewNudge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const NAVY = "#0A1F44";
const GOLD = "#C9A24A";

const CATEGORY_ICON = {
  identity:     IdCard,
  compliance:   ShieldCheck,
  learning:     GraduationCap,
  financial:    Wallet,
  professional: Briefcase,
  gaming:       Shield,
  other:        Award,
};

const STATUS_TONE = {
  issued:   { label: "Active",  bg: "#D1FAE5", color: "#065F46" },
  active:   { label: "Active",  bg: "#D1FAE5", color: "#065F46" },
  shared:   { label: "Shared",  bg: "#DBEAFE", color: "#1E40AF" },
  expired:  { label: "Expired", bg: "#FEE2E2", color: "#7F1D1D" },
  revoked:  { label: "Revoked", bg: "#FEE2E2", color: "#7F1D1D" },
  archived: { label: "Archived",bg: "#F1F5F9", color: "#475569" },
  draft:    { label: "Draft",   bg: "#F1F5F9", color: "#334155" },
};

export default function WalletPassportPage() {
  const params = useParams();
  const [consumer_id, setConsumerId] = useState(params.consumer_id || null);
  const [passport, setPassport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);   // { exists, profile? }

  useEffect(() => {
    if (!params.consumer_id) {
      const w = loadWallet();
      setConsumerId(w?.consumer_id || null);
    }
  }, [params.consumer_id]);

  useEffect(() => {
    const w = loadWallet();
    const device_id = w?.device_id;
    if (!device_id) { setProfile({ exists: false }); return; }
    axios.get(`${BACKEND_URL}/api/wallet/financial-profile`, { params: { device_id } })
      .then(r => setProfile({ exists: !!r.data.exists, profile: r.data.profile }))
      .catch(() => setProfile({ exists: false }));
  }, []);

  const load = useCallback(async () => {
    if (!consumer_id) { setLoading(false); return; }
    try {
      const { data } = await axios.get(
        `${BACKEND_URL}/api/wallet/credentials/${consumer_id}/passport`,
      );
      setPassport(data);
    } catch {
      toast.error("Could not load your Trust Wallet");
    } finally {
      setLoading(false);
    }
  }, [consumer_id]);

  useEffect(() => { load(); }, [load]);

  async function shareCredential(credential_id) {
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/wallet/credentials/${consumer_id}/${credential_id}/share`,
        { method: "link", max_uses: 5, expires_in_hours: 24 },
      );
      const fullUrl = `${window.location.origin}${data.share_url}`;
      await navigator.clipboard?.writeText(fullUrl);
      toast.success("Share link copied to clipboard");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Share failed");
    }
  }

  if (loading) {
    return (
      <WalletShell activeTab="passport">
        <div className="max-w-2xl mx-auto p-10 text-sm text-slate-500" data-testid="wallet-passport-loading">
          Loading your Trust Wallet…
        </div>
      </WalletShell>
    );
  }

  if (!consumer_id) {
    return (
      <WalletShell activeTab="passport">
        <div className="bg-slate-50 pb-16" data-testid="wallet-passport-no-consumer">
          <PassportCoverStub />
          <div className="max-w-2xl mx-auto p-4 space-y-6">
            <PassportExplainer />
            <PassportReviewNudge />
            <FinancialProfileCard profile={profile} />
            <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-8 text-center space-y-3">
              <ShieldCheck className="w-6 h-6 text-slate-300 mx-auto" />
              <div className="text-[14px] font-semibold text-slate-700">Set up your Trust Wallet</div>
              <div className="text-[12px] text-slate-500 max-w-md mx-auto">
                Your Trust Wallet is your portable, evidence-backed profile. Open a wallet session to view your credentials.
              </div>
              <Link
                to="/wallet/onboarding"
                data-testid="wallet-passport-onboard-cta"
                className="inline-flex items-center gap-1 text-[12px] font-semibold px-3 py-2 rounded-md text-white"
                style={{ background: NAVY }}
              >
                Start onboarding
              </Link>
            </div>
          </div>
        </div>
      </WalletShell>
    );
  }

  if (!passport) {
    return (
      <WalletShell activeTab="passport">
        <div className="max-w-2xl mx-auto p-10 text-sm text-slate-500">
          Could not load Trust Wallet.
        </div>
      </WalletShell>
    );
  }

  return (
    <WalletShell activeTab="passport">
    <div className="bg-slate-50 pb-16" data-testid="wallet-passport">
      {/* Passport cover */}
      <header
        className="text-white"
        style={{ background: `linear-gradient(135deg, ${NAVY}, #14304F)` }}
      >
        <div className="max-w-2xl mx-auto p-6 space-y-3">
          <div className="text-[10px] uppercase tracking-[0.24em] font-semibold" style={{ color: GOLD }}>
            Consumer Trust Wallet · My Trust Wallet
          </div>
          <h1 className="font-display text-3xl font-semibold">Your Trust Wallet</h1>
          <p className="text-[13px] text-slate-200 max-w-xl leading-relaxed">
            A portable, evidence-backed profile of every trust credential you&apos;ve earned. Share it once — every participating institution recognises it.
          </p>
          <div className="grid grid-cols-3 gap-2 pt-4">
            <PassportStat label="Credentials" value={passport.credentials_total} />
            <PassportStat label="Active"      value={passport.credentials_active} />
            <PassportStat label="Institutions" value={passport.connected_institutions.length} />
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto p-4 space-y-6">
        {/* What is the Trust Wallet? — always visible explainer */}
        <PassportExplainer />

        {/* Review nudge — surfaces overdue or missing critical documents */}
        <PassportReviewNudge />

        {/* Financial Profile — the consumer's own facts, always visible */}
        <FinancialProfileCard profile={profile} />

        {/* Empty state */}
        {passport.credentials_total === 0 && (
          <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-white p-8 text-center">
            <ShieldCheck className="w-6 h-6 text-slate-300 mx-auto" />
            <div className="text-[14px] font-semibold text-slate-600 mt-2">No credentials yet</div>
            <div className="text-[12px] text-slate-500 mt-1">
              When an institution completes a Trust Request with you, credentials appear here automatically.
            </div>
          </div>
        )}

        {/* Grouped credentials */}
        {Object.entries(passport.credentials_by_category).map(([cat, list]) => {
          const Icon = CATEGORY_ICON[cat] || Award;
          return (
            <section
              key={cat}
              data-testid={`passport-cat-${cat}`}
              className="rounded-2xl bg-white border border-slate-100 shadow-sm p-4 space-y-2"
            >
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${GOLD}22`, color: GOLD }}>
                  <Icon className="w-4 h-4" />
                </div>
                <div>
                  <div className="font-display text-lg font-semibold capitalize" style={{ color: NAVY }}>
                    {cat}
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-400">
                    {list.length} credential{list.length === 1 ? "" : "s"}
                  </div>
                </div>
              </div>
              <div className="space-y-2 pt-2 border-t border-slate-100">
                {list.map((c) => <CredentialRow key={c.credential_id} cred={c} onShare={() => shareCredential(c.credential_id)} />)}
              </div>
            </section>
          );
        })}

        {/* Connected institutions */}
        {passport.connected_institutions.length > 0 && (
          <section data-testid="passport-institutions" className="rounded-2xl bg-white border border-slate-100 shadow-sm p-4">
            <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-2">
              Connected institutions
            </div>
            <div className="flex flex-wrap gap-2">
              {passport.connected_institutions.map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-slate-700 border border-slate-200 rounded-full px-3 py-1.5 bg-slate-50">
                  <Building2 className="w-3.5 h-3.5" /> {t}
                </span>
              ))}
            </div>
          </section>
        )}

        {/* Sharing history */}
        {passport.sharing_history.length > 0 && (
          <section data-testid="passport-sharing-history" className="rounded-2xl bg-white border border-slate-100 shadow-sm p-4">
            <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-2">
              Sharing history
            </div>
            <ul className="space-y-1.5">
              {passport.sharing_history.slice(0, 10).map((s) => (
                <li key={s.share_id} className="flex items-center gap-2 text-[12px] text-slate-700">
                  <Share2 className="w-3.5 h-3.5 text-slate-400" />
                  <span className="font-mono text-slate-500">…{s.token_display}</span>
                  <span className="text-slate-400">·</span>
                  <span className="capitalize">{s.method}</span>
                  <span className="text-slate-400">·</span>
                  <span>{s.use_count}/{s.max_uses} uses</span>
                  {s.revoked_at && <span className="text-red-700">· revoked</span>}
                  <span className="ml-auto text-[10px] text-slate-400 font-mono">
                    {formatDistanceToNow(new Date(s.created_at), { addSuffix: true })}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}
      </main>
    </div>
    </WalletShell>
  );
}

function PassportStat({ label, value }) {
  return (
    <div className="rounded-xl bg-white/10 p-3 backdrop-blur-sm">
      <div className="text-[9px] uppercase tracking-widest text-slate-300 font-semibold">{label}</div>
      <div className="font-display text-2xl font-semibold mt-0.5">{value}</div>
    </div>
  );
}

function CredentialRow({ cred, onShare }) {
  const tone = STATUS_TONE[cred.status] || STATUS_TONE.issued;
  const expiresLabel = cred.expires_at
    ? `Expires ${format(new Date(cred.expires_at), "d MMM yyyy")}`
    : "No expiry";
  return (
    <div
      data-testid={`credential-${cred.credential_id}`}
      className="rounded-xl border border-slate-100 p-3 space-y-2"
    >
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-semibold text-[14px]" style={{ color: NAVY }}>{cred.display_name}</div>
            <span className="text-[9px] uppercase tracking-widest font-mono px-1.5 py-0.5 rounded"
              style={{ background: tone.bg, color: tone.color }}>{tone.label}</span>
            {cred.verification_level === "verified" && (
              <ClipboardCheck className="w-3.5 h-3.5 text-emerald-600" />
            )}
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            Issued by <strong className="text-slate-700">{cred.issuer_name}</strong>
            {" · "}{expiresLabel}
            {" · "}Level: <span className="font-mono text-slate-500">{cred.verification_level.replace(/_/g, " ")}</span>
          </div>
          <div className="text-[10px] text-slate-400 font-mono mt-1">
            {cred.credential_id}
          </div>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 pt-1 border-t border-slate-100">
        <button
          data-testid={`share-${cred.credential_id}`}
          disabled={!cred.shareable || cred.status === "revoked" || cred.status === "expired"}
          onClick={onShare}
          className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-md bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Link2 className="w-3 h-3" /> Share link
        </button>
        <a
          data-testid={`view-${cred.credential_id}`}
          href={`/verify/credential-id/${cred.credential_id}`}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-md bg-slate-100 text-slate-700 hover:bg-slate-200"
        >
          <Copy className="w-3 h-3" /> View
        </a>
      </div>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════
 * Trust Wallet explainer + Financial Profile summary
 * ════════════════════════════════════════════════════════ */

const INCOME_LABEL = {
  under_5k:  "Under R5 000",
  "5k_15k":  "R5 000 – R15 000",
  "15k_30k": "R15 000 – R30 000",
  "30k_60k": "R30 000 – R60 000",
  "60k_120k":"R60 000 – R120 000",
  "120k_plus":"R120 000 or more",
};

const EMP_LABEL = {
  employed: "Employed", self_employed: "Self-employed",
  unemployed: "Unemployed", retired: "Retired", student: "Student",
};

function PassportCoverStub() {
  return (
    <header className="text-white" style={{ background: `linear-gradient(135deg, ${NAVY}, #14304F)` }}>
      <div className="max-w-2xl mx-auto p-6 space-y-3">
        <div className="text-[10px] uppercase tracking-[0.24em] font-semibold" style={{ color: GOLD }}>
          Consumer Trust Wallet · My Trust Wallet
        </div>
        <h1 className="font-display text-3xl font-semibold">Your Trust Wallet</h1>
      </div>
    </header>
  );
}

function PassportExplainer() {
  return (
    <section
      data-testid="passport-explainer"
      className="rounded-2xl bg-white border border-slate-100 shadow-sm p-5 space-y-4"
    >
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${GOLD}22`, color: GOLD }}>
          <Info className="w-4 h-4" />
        </div>
        <div className="font-display text-lg font-semibold" style={{ color: NAVY }}>
          What is the Trust Wallet?
        </div>
      </div>

      <p className="text-[13.5px] text-slate-700 leading-relaxed">
        Your Trust Wallet is your <strong>portable, evidence-backed profile</strong>. It
        is not a bank account and it does not hold money. It is the one place where
        everything a financial institution has verified, offered or asked of you
        lives — so <em>you</em> stay in control of your own story.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <ExplainerTile
          Icon={IdCard}
          title="What it collects"
          body="Verified identity attributes, your Financial Profile, learning credentials, product-understanding scores and any documents your advisors drop into your wallet."
        />
        <ExplainerTile
          Icon={ShieldCheck}
          title="Why it exists"
          body="So an institution can trust you (and you can trust them) without asking you to re-prove yourself every time — no more repeated KYC, no more phishing links."
        />
        <ExplainerTile
          Icon={Share2}
          title="How it stays yours"
          body="Every share is a signed, revocable credential. You decide who sees what and for how long. Everything is hash-chained — nothing can be quietly altered."
        />
      </div>

      <div className="rounded-lg bg-slate-50 border border-slate-100 p-3 text-[12px] text-slate-600 leading-relaxed">
        <strong className="text-slate-800">Where the data comes from:</strong>{" "}
        You supply the Financial Profile yourself. Credentials arrive only after
        an institution has verified something for you (a KYC check, a completed
        learning module, an advisor-issued proposal). Nothing is copied from
        your other banks without your explicit consent.
      </div>
    </section>
  );
}

function ExplainerTile({ Icon, title, body }) {
  return (
    <div className="rounded-xl border border-slate-100 p-3">
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-3.5 h-3.5" style={{ color: NAVY }} />
        <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: NAVY }}>{title}</div>
      </div>
      <p className="text-[12px] text-slate-600 leading-relaxed">{body}</p>
    </div>
  );
}

function FinancialProfileCard({ profile }) {
  const state = profile;
  return (
    <section
      data-testid="passport-financial-profile"
      className="rounded-2xl bg-white border border-slate-100 shadow-sm p-5 space-y-3"
    >
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${GOLD}22`, color: GOLD }}>
          <UserCog className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-lg font-semibold" style={{ color: NAVY }}>Financial Profile</div>
          <div className="text-[11px] text-slate-500">
            Five facts about your situation — powers every affordability check in the wallet.
          </div>
        </div>
        {state?.exists ? (
          <span className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-semibold uppercase tracking-widest"
            style={{ background: "#D1FAE5", color: "#065F46" }}>
            <CheckCircle2 className="w-3 h-3" /> Saved
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-semibold uppercase tracking-widest"
            style={{ background: "#FEF3C7", color: "#92400E" }}>
            Missing
          </span>
        )}
      </div>

      {state?.exists && state.profile ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <ProfileFact Icon={CalendarClock} label="Age band" value={state.profile.age_band} />
          <ProfileFact Icon={Briefcase}     label="Employment" value={EMP_LABEL[state.profile.employment] || state.profile.employment} />
          <ProfileFact Icon={Wallet}        label="Monthly income" value={INCOME_LABEL[state.profile.monthly_income_band] || state.profile.monthly_income_band} />
          <ProfileFact Icon={Wallet}        label="Monthly savings" value={`R ${Math.round(state.profile.monthly_savings_zar || 0).toLocaleString()}`} />
          <ProfileFact Icon={Users}         label="Dependants" value={state.profile.dependants} />
        </div>
      ) : (
        <p className="text-[13px] text-slate-600 leading-relaxed">
          Complete your Financial Profile so that every quote, proposal or product
          check in the wallet can honestly tell you whether it fits your situation.
          Five quick questions, ~60 seconds — nothing shared without your consent.
        </p>
      )}

      <Link
        to="/wallet/profile"
        data-testid="passport-profile-cta"
        className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-[12px] font-semibold text-white"
        style={{ background: NAVY }}
      >
        {state?.exists ? "Update my Financial Profile" : "Complete my Financial Profile"}
        <ArrowRight className="w-3.5 h-3.5" />
      </Link>
    </section>
  );
}

function ProfileFact({ Icon, label, value }) {
  return (
    <div className="rounded-lg border border-slate-100 px-3 py-2">
      <div className="flex items-center gap-1 text-[9px] uppercase tracking-widest text-slate-400 font-semibold">
        <Icon className="w-2.5 h-2.5" /> {label}
      </div>
      <div className="text-[13px] font-semibold mt-0.5" style={{ color: NAVY }}>{value}</div>
    </div>
  );
}

